import { getApplicationSettings } from 'owa-application-settings';
import { getOrigin } from 'owa-url/lib/getOrigin';
import getRootVdirName from 'owa-url/lib/getRootVdirName';
import { isConsumer } from 'owa-session-store';
import { getQueryStringParameter } from 'owa-querystring';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';

const OWA_CONSUMER_APP_ID = '292841';
const SELECT_ACCOUNT_PARAM = 'prompt=select_account';
const LOGIN_HINT_PARAM = 'login_hint';
const APPEND_BOOT_FLIGHTS_PARAM = 'appendbootflights';
const OWA_ENTERPRISE_URL = 'https://outlook.office.com';
const OWA_APP_ID = '9199bf20-a13f-4107-85dc-02114787ef48';
const MSA_LOGIN_URL = 'https://login.live.com/Me.srf';
export const AAD_FORGET_USER_URL = 'https://login.microsoftonline.com/forgetuser';
export const MSA_FORGET_USER_URL = 'https://login.live.com/forgetuser';

/**
 * Gets the remembered-accounts iframe URLs shared by the Me Control implementations.
 */
export function getRememberedAccountsUrls(): { aadUrl?: string; msaUrl?: string } {
    const origin = getOrigin();
    return isConsumer()
        ? { msaUrl: buildMsaRememberedAccountsUrl(origin) }
        : { aadUrl: buildAadRememberedAccountsUrl(origin) };
}

/**
 * Builds the MSA remembered accounts URL with query parameters
 */
export function buildMsaRememberedAccountsUrl(origin: string): string {
    const params = new URLSearchParams({
        wa: 'wsignin1.0',
        id: OWA_CONSUMER_APP_ID,
        wreply: `${origin}/${getRootVdirName()}/`,
        idpflag: 'proxy',
    });
    return `${MSA_LOGIN_URL}?${params.toString()}`;
}

/**
 * Builds the AAD remembered accounts URL with query parameters
 */
export function buildAadRememberedAccountsUrl(origin: string): string {
    const params = new URLSearchParams({
        wreply: origin,
        appid: OWA_APP_ID,
        idpflag: 'proxy',
    });
    return `${
        getApplicationSettings('MeControl').aadRememberedAccountsUrl
    }/savedusers?${params.toString()}`;
}

/**
 * Gets the AAD login domain
 */
function getAadLoginDomain(): string {
    // When the currently logged in user is an aad user, we want to use the same domain for logging into aad,
    // to account for scenarios where outlook.office.com domain could be blocked for the user.
    // If the MSAL consumer boot flight is enabled, the current domain will be returned as client-side MSAL can handle account switching.
    // When the logged in user is an MSA type, since the aad domain would be different from the user domain,
    // we are explicitly passing https://outlook.office.com
    if (!isConsumer()) {
        return getOrigin();
    }

    return OWA_ENTERPRISE_URL;
}

/**
 * Gets the reply-to URL for authentication
 */
function getReplyToUrl(): string {
    return `${getOrigin()}/${getRootVdirName()}/`;
}

/**
 * Gets the sign-out URL
 */
export function getAppSignOutUrl(): string {
    return `${getReplyToUrl()}logoff.owa`;
}

/**
 * Gets the URL for signing in to a different account
 */
export function getSignInToDifferentAccountUrl(): string {
    return `${getOrigin()}/${getRootVdirName()}/?${SELECT_ACCOUNT_PARAM}${getAppendBootFlightsParam()}`;
}

/**
 * Gets the URL for switching to a specific account
 */
export function getAppSwitchToUrl(params?: {
    nextAccount?: { type?: string; memberName: string; login_hint?: string };
}): string {
    if (!params || !params.nextAccount) {
        return getSignInToDifferentAccountUrl();
    }

    // This aad login url is used when the account selected is an aad account type.
    const aadLogin = getAadLoginDomain();

    // This is the login domain used for switching to the selected account. This depends on the account type - when selected account
    // is aad type, aadLogin would be used, and for consumer type, live.com login would be used instead.
    const domain =
        params.nextAccount.type?.toLowerCase() == 'aad' ? aadLogin : 'https://outlook.live.com';

    const account =
        (isGlobalFeatureEnabled('auth-login-hint-mecontrol') && params.nextAccount.login_hint) ||
        encodeURIComponent(params.nextAccount.memberName);
    const appSwitchToUrl = `${domain}/${getRootVdirName()}/?${LOGIN_HINT_PARAM}=${account}${getAppendBootFlightsParam()}`;

    return appSwitchToUrl;
}

/**
 * Gets the append boot flights query parameter
 */
function getAppendBootFlightsParam(): string {
    // Enable internal testing with 'appendbootflights' query parameter
    const bootflights = getQueryStringParameter(APPEND_BOOT_FLIGHTS_PARAM);
    const appendBootFlightParam = bootflights ? `&${APPEND_BOOT_FLIGHTS_PARAM}=${bootflights}` : '';

    return appendBootFlightParam;
}
